import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = "#69d2c7";
const GREEN = "#22c55e";
const CARD_CHAR_LIMIT = 220;

function Star({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className="shrink-0" aria-hidden="true">
      <path d="M12 2l2.9 6.26 6.9.8-5.1 4.7 1.36 6.8L12 17.2l-6.06 3.36 1.36-6.8-5.1-4.7 6.9-.8L12 2z" />
    </svg>
  );
}

export function PartialStar({ state, size = 16 }) {
  return (
    <span className="relative inline-block leading-none">
      <span className="block text-neutral-300">
        <Star size={size} />
      </span>
      {state !== "empty" && (
        <span
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: state === "full" ? "100%" : "50%" }}
        >
          <span className="block text-neutral-950">
            <Star size={size} />
          </span>
        </span>
      )}
    </span>
  );
}

export function starState(value, n) {
  if (value >= n) return "full";
  if (value >= n - 0.5) return "half";
  return "empty";
}

export function StarRow({ className = "", size = 18 }) {
  return (
    <div className={`flex gap-0.5 ${className}`} style={{ width: "max-content" }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} />
      ))}
    </div>
  );
}

function truncateAtWord(text, limit) {  if (text.length <= limit) return null;
  const cut = text.slice(0, limit);
  const i = cut.lastIndexOf(" ");
  return (i > 0 ? cut.slice(0, i) : cut).replace(/[.,;:!?]+$/, "") + "…";
}

export function ReviewsAggregate({ avg, total, className = "mt-5" }) {
  return (
    <div className={`${className} flex items-center justify-center gap-3`} data-testid="reviews-aggregate">
      <div className="relative" aria-hidden="true">
        <StarRow className="text-neutral-300" />
        <div
          className="absolute top-0 left-0 h-full overflow-hidden"
          style={{ width: `${(avg / 5) * 100}%` }}
          data-testid="reviews-aggregate-fill"
        >
          <StarRow className="text-neutral-950" />
        </div>
      </div>
      <p className="text-sm font-semibold" data-testid="reviews-aggregate-text">
        {Number(avg).toFixed(1)} / 5 · {total} reader review{total === 1 ? "" : "s"}
      </p>
    </div>
  );
}

export function ReviewCard({ r, i, showDate = false }) {
  const [expanded, setExpanded] = useState(false);
  const truncated = truncateAtWord(r.text, CARD_CHAR_LIMIT);
  const shown = truncated && !expanded ? truncated : r.text;
  const submittedOn =
    showDate && r.created_at && !isNaN(new Date(r.created_at).getTime())
      ? new Date(r.created_at).toLocaleDateString("en-GB", {
          timeZone: "UTC",
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "";

  return (
    <div className="border border-neutral-200 bg-neutral-50 p-6 flex flex-col text-left h-full w-full" data-testid={`review-card-${i}`}>
      <div className="flex gap-0.5" aria-label={`${r.rating} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <PartialStar key={n} state={starState(r.rating, n)} size={16} />
        ))}
      </div>
      <p className="mt-4 text-base leading-relaxed flex-1 whitespace-pre-wrap" data-testid={`review-card-text-${i}`}>
        “{shown}”
      </p>
      {truncated && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-2 self-start text-sm font-semibold underline underline-offset-4 decoration-2 hover:text-neutral-600"
          style={{ textDecorationColor: TEAL }}
          data-testid={`review-card-toggle-${i}`}
        >
          {expanded ? "Show less" : "Show full review"}
        </button>
      )}
      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold" data-testid={`review-card-name-${i}`}>
            {r.name || "Anonymous trader"}
          </p>
          {submittedOn && (
            <p className="text-xs" style={{ color: "#6E6A66" }} data-testid={`review-card-date-${i}`}>
              {submittedOn}
            </p>
          )}
        </div>
        {r.verified_download && (
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold"
            style={{ color: GREEN }}
            data-testid={`review-card-verified-${i}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            Verified download
          </span>
        )}
      </div>
    </div>
  );
}

export default function ReaderReviews() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/reviews`)
      .then((res) => {
        if (alive) setData(res.data);
      })
      .catch(() => {
        if (alive) setData({ reviews: [], average_rating: 0, total_count: 0 });
      });
    return () => {
      alive = false;
    };
  }, []);

  const loaded = data !== null;
  const total = loaded ? data.total_count : 0;
  const avg = loaded ? data.average_rating : 0;
  // Newest-first order comes from the API; cap the DOM at DESKTOP_MAX and hide overflow on mobile via responsive classes
  const MOBILE_MAX = 3;
  const DESKTOP_MAX = 6;
  const cards = loaded ? data.reviews.slice(0, DESKTOP_MAX) : [];

  return (
    <section className="border-t border-neutral-200" data-testid="landing-reviews">
      <div className="max-w-screen-lg mx-auto px-5 md:px-8 py-12 md:py-16">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">Reader reviews</h2>
        <p className="mt-3 text-sm text-neutral-500 text-center" data-testid="reviews-moderation-note">
          Reviews are moderated for spam, abuse, and personal information.
        </p>

        {loaded && total >= 5 && <ReviewsAggregate avg={avg} total={total} />}

        {loaded && total === 0 && (
          <p className="mt-8 text-center text-base text-neutral-600" data-testid="reviews-empty-state">
            <Link
              to="/leave"
              className="underline underline-offset-4 decoration-2 hover:text-neutral-950"
              style={{ textDecorationColor: TEAL }}
            >
              No reviews yet. Read the book, then be the first.
            </Link>
          </p>
        )}

        {cards.length > 0 && (
          <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="landing-reviews-grid">
            <div className="border border-neutral-200 bg-white p-6 flex flex-col items-center justify-center text-center" data-testid="reviews-leave-review-card">
              <p className="text-base font-semibold">Read the book?</p>
              <Link
                to="/leave"
                className="mt-2 text-base font-semibold underline underline-offset-4 decoration-2 hover:text-neutral-600"
                style={{ textDecorationColor: TEAL }}
                data-testid="reviews-leave-review-link"
              >
                Leave an honest review →
              </Link>
            </div>
            {cards.map((r, i) => (
              <div
                key={i}
                className={i >= MOBILE_MAX ? "hidden sm:block" : ""}
                data-testid={`review-card-wrapper-${i}`}
              >
                <ReviewCard r={r} i={i} />
              </div>
            ))}
          </div>
        )}

        {total > MOBILE_MAX && (
          <p className="mt-10 text-center sm:hidden" data-testid="reviews-see-all-mobile">
            <Link
              to="/reviews"
              className="text-base font-semibold underline underline-offset-4 decoration-2 hover:text-neutral-600"
              style={{ textDecorationColor: TEAL }}
            >
              See all {total} reviews →
            </Link>
          </p>
        )}
        {total > DESKTOP_MAX && (
          <p className="mt-10 text-center hidden sm:block" data-testid="reviews-see-all-desktop">
            <Link
              to="/reviews"
              className="text-base font-semibold underline underline-offset-4 decoration-2 hover:text-neutral-600"
              style={{ textDecorationColor: TEAL }}
            >
              See all {total} reviews →
            </Link>
          </p>
        )}
      </div>
    </section>
  );
}
